import { PurchaseOrderAllocation } from '../../usePurchaseOrderAllocations';
import styles from './PurchaseOrderAllocations.module.css';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoney, showNotification } from 'helpers';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { getCampaignLink } from 'Modules/Plans/Campaigns/helpers';
import { Button, assets } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import AllocationInput from '../AllocationInput/AllocationInput';
import { useState } from 'react';
import { useUpdatePurchaseOrderLine } from '@round/creatorbase-helpers/api-hooks';
import { VirtualList } from '@round/whitelabel-ui';
import { AppIcon } from 'Modules/Plans/components/ApplicationBadge/ApplicationBadge';
import { Skeleton } from '@round/ui-kit';
import range from 'lodash/range';
import PlatformIcon from 'Modules/Plans/components/PlatformIcon';
import { TooltipOnOverflow } from '@round/whitelabel-ui';

const { Trash: TrashIcon } = assets;

type Props = {
    allocations: PurchaseOrderAllocation[];
    isLoading: boolean;
    error: string | null;
    hasNextPage: boolean;
    fetchNextPage: () => void;
} & Pick<AllocationRowProps, 'purchaseOrder' | 'onUpdated' | 'onDelete'>;

const TITLE = 'Current Allocations';

const PurchaseOrderAllocations = ({
    allocations,
    error,
    isLoading,
    purchaseOrder,
    onDelete,
    onUpdated,
    hasNextPage,
    fetchNextPage,
}: Props) => {
    if (error) {
        return (
            <div className={styles.container}>
                <p className={styles.title}>{TITLE}</p>
                <p className={cn(styles.error, styles.row)}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <p className={styles.title}>{TITLE}</p>

            {isLoading && !allocations.length ? (
                range(5).map(() => (
                    <div className={styles.row}>
                        <Skeleton className={styles.skeleton} containerClassName={styles.skeletonContainer} />
                    </div>
                ))
            ) : allocations.length ? (
                <VirtualList
                    className={styles.scrollView}
                    data={allocations}
                    options={{ count: allocations.length, estimateSize: () => 52, gap: 8 }}
                    onEndReached={() => {
                        if (hasNextPage && !isLoading) {
                            fetchNextPage();
                        }
                    }}
                >
                    {(allocation) => (
                        <AllocationRow
                            key={`${allocation.id}${allocation.amount}`}
                            allocation={allocation}
                            purchaseOrder={purchaseOrder}
                            onDelete={onDelete}
                            onUpdated={onUpdated}
                            isLoading={isLoading}
                        />
                    )}
                </VirtualList>
            ) : (
                <p className={styles.empty}>No allocations yet</p>
            )}
        </div>
    );
};

type AllocationRowProps = {
    isReadonly?: boolean;
    allocation: PurchaseOrderAllocation;
    purchaseOrder: creatorbase.PurchaseOrder | null | undefined;
    onDelete: (allocation: PurchaseOrderAllocation) => void;
    onUpdated: (updatedAllocation: PurchaseOrderAllocation) => void;
    isLoading: boolean;
};

const AllocationRow = ({
    isReadonly,
    allocation,
    purchaseOrder,
    onUpdated,
    onDelete,
    isLoading,
}: AllocationRowProps) => {
    const { getCurrencyById } = useCurrencies();
    const [amount, setAmount] = useState<string | undefined>(allocation.amount.toString());
    const { update, isLoading: isUpdating, helpers } = useUpdatePurchaseOrderLine(allocation.id);

    const campaignUnallocated =
        (allocation.campaign?.cost ?? 0) - (allocation.campaign?.purchase_orders_allocated_amount ?? 0);
    const max = Math.min(
        allocation.amount + (purchaseOrder?.remaining_amount ?? 0),
        campaignUnallocated + allocation.amount
    );

    const isAmountLargerThanMaxValue = parseFloat(amount ?? '0') > max;

    const submit = () => {
        const parsedAmount = parseFloat(amount || '');
        if (!isFinite(parsedAmount)) {
            showNotification('Must be a number', 'error');
            return;
        }

        if (parsedAmount === allocation.amount) {
            return;
        }

        update(
            { id: allocation.id, amount: parsedAmount },
            {
                onSuccess: (data) => {
                    onUpdated({ ...allocation, ...data });
                    showNotification('Updated', 'info');
                },
                onError: (error) => {
                    showNotification(helpers.getErrorMessage(error), 'error');
                },
            }
        );
    };

    const campaignDetails = `${allocation.campaign?.name} - ${allocation.project?.name}`;
    const brandDetails = `${allocation.brand?.name} - ${allocation.team?.name}`;

    if (isLoading) {
        return (
            <div className={styles.row}>
                <div className={styles.detailsContainer}>
                    <Skeleton className={styles.skeleton} containerClassName={styles.skeletonContainer} />
                    <Skeleton className={styles.skeleton} containerClassName={styles.skeletonContainer} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={styles.row}>
                <div className={styles.detailsContainer}>
                    <div className={styles.campaignContainer}>
                        {allocation.campaign?.managed_by && allocation.campaign.managed_by !== 'creatorbase' && (
                            <span className={styles.appIconContainer}>
                                <AppIcon className={styles.appIcon} application={allocation.campaign.managed_by} />
                            </span>
                        )}

                        {!!allocation.campaign?.platform && (
                            <PlatformIcon
                                platform={allocation.campaign?.platform}
                                colored
                                className={styles.platformIcon}
                            />
                        )}

                        <TooltipOnOverflow tooltip={campaignDetails}>
                            <Link
                                className={styles.campaignLink}
                                to={getCampaignLink(allocation.campaign)}
                                target="_blank"
                            >
                                {campaignDetails}
                            </Link>
                        </TooltipOnOverflow>
                    </div>
                    <TooltipOnOverflow tooltip={brandDetails}>
                        <p className={styles.subDetail}>{brandDetails}</p>
                    </TooltipOnOverflow>
                </div>

                <div className={styles.actions}>
                    <AllocationInput
                        readOnly={isReadonly}
                        className={styles.allocationInput}
                        disabled={isUpdating}
                        max={max}
                        value={amount}
                        onValueChange={setAmount}
                        currencyId={allocation.campaign?.currency_id}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                submit();
                            }
                        }}
                        onBlur={submit}
                    />
                    {!isReadonly && (
                        <Button
                            className={cn(styles.button, styles.deleteButton)}
                            appearance="ghost"
                            type="button"
                            onClick={() => onDelete(allocation)}
                        >
                            <TrashIcon />
                        </Button>
                    )}
                </div>
            </div>
            {isAmountLargerThanMaxValue && (
                <p className={styles.maxAmountWarning}>
                    Max: {asMoney(max, getCurrencyById(purchaseOrder?.currency_id))}
                </p>
            )}
        </>
    );
};

export default PurchaseOrderAllocations;
